import { useCallback } from "react";
import type { Feature, BoxFeature, CylinderFeature, SphereFeature, BooleanFeature, ModifierFeature, ExtrudeFeature, LoftFeature, RevolveFeature, SweepFeature, TransformFeature, MirrorFeature, LinearPatternFeature, CircularPatternFeature, ProfileType, AxisName, MirrorPlaneName, Vec3, MateConstraint } from "./featureTypes";
import { isBooleanFeature, isModifierFeature, isTransformFeature, isAssemblyPatternFeature, isSheetMetalFeature } from "./featureTypes";
import { MATERIAL_PRESETS, PRESET_KEYS } from "../renderer/materials";
import { STANDARD_PARTS_CATALOG, stdPartDocId, stdPartName } from "./standardParts";

const MATERIAL_PRESETS_MAP: Record<string, string> = Object.fromEntries(
  PRESET_KEYS.map((k) => [k, MATERIAL_PRESETS[k].label])
);

interface FeaturePropertyPanelProps {
  feature: Feature;
  features: Feature[];
  onUpdate: (id: string, changes: Partial<Feature>) => void;
  onRemoveMate?: (featureId: string, mateId: string) => void;
  onUpdateMate?: (featureId: string, mateId: string, changes: Partial<MateConstraint>) => void;
}

export default function FeaturePropertyPanel({ feature, features, onUpdate, onRemoveMate, onUpdateMate }: FeaturePropertyPanelProps) {
  return (
    <div className="feature-property-panel">
      <div className="param-panel-header">
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor">
          <path d="M2 4h3M9 4h5M2 8h7M13 8h1M2 12h1M7 12h7" />
          <circle cx="7" cy="4" r="1.5" />
          <circle cx="11" cy="8" r="1.5" />
          <circle cx="5" cy="12" r="1.5" />
        </svg>
        Properties
      </div>
      <NameRow feature={feature} onUpdate={onUpdate} />
      {feature.type === "box" && <BoxProps feature={feature} onUpdate={onUpdate} />}
      {feature.type === "cylinder" && <CylinderProps feature={feature} onUpdate={onUpdate} />}
      {feature.type === "sphere" && <SphereProps feature={feature} onUpdate={onUpdate} />}
      {isBooleanFeature(feature) && (
        <BooleanProps feature={feature} features={features} onUpdate={onUpdate} />
      )}
      {isModifierFeature(feature) && (
        <ModifierProps feature={feature} features={features} onUpdate={onUpdate} />
      )}
      {feature.type === "extrude" && (
        <ExtrudeProps feature={feature as ExtrudeFeature} onUpdate={onUpdate} />
      )}
      {feature.type === "sketch" && (
        <SketchOperationProps feature={feature as import("./featureTypes").SketchFeature} onUpdate={onUpdate} />
      )}
      {feature.type === "importedPart" && (
        <ImportedPartProps feature={feature as import("./featureTypes").ImportedPartFeature} onUpdate={onUpdate} onRemoveMate={onRemoveMate} onUpdateMate={onUpdateMate} />
      )}
      {feature.type === "loft" && (
        <LoftProps feature={feature as LoftFeature} onUpdate={onUpdate} />
      )}
      {feature.type === "revolve" && (
        <RevolveProps feature={feature as RevolveFeature} onUpdate={onUpdate} />
      )}
      {feature.type === "sweep" && (
        <SweepProps feature={feature as SweepFeature} onUpdate={onUpdate} />
      )}
      {isTransformFeature(feature) && (
        <TransformProps feature={feature as TransformFeature} features={features} onUpdate={onUpdate} />
      )}
      {isAssemblyPatternFeature(feature) && (
        <AssemblyPatternProps feature={feature} features={features} onUpdate={onUpdate} />
      )}
      {isSheetMetalFeature(feature) && (
        <SheetMetalProps feature={feature} features={features} onUpdate={onUpdate} />
      )}
      <MaterialSelect feature={feature} onUpdate={onUpdate} />
      {"position" in feature && (
        <PositionProps
          position={(feature as { position: Vec3 }).position}
          onChange={(pos) => onUpdate(feature.id, { position: pos } as Partial<Feature>)}
        />
      )}
    </div>
  );
}

// ── Shared row component ────────────────────────────────

function DimRow({
  label,
  value,
  min = 0.1,
  max = 200,
  onChange,
  labelClass,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (v: number) => void;
  labelClass?: string;
}) {
  const step = (max - min) / 100;

  const handleSlider = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onChange(parseFloat(e.target.value)),
    [onChange]
  );

  const handleInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = parseFloat(e.target.value);
      if (!isNaN(v)) onChange(v);
    },
    [onChange]
  );

  return (
    <div className="param-row">
      <label className={`param-label ${labelClass ?? ""}`}>{label}</label>
      <input type="range" className="param-slider" min={min} max={max} step={step} value={value} onChange={handleSlider} />
      <input type="number" className="param-input" min={min} max={max} step={step} value={value} onChange={handleInput} />
    </div>
  );
}

// ── Name input ──────────────────────────────────────────

function NameRow({ feature, onUpdate }: { feature: Feature; onUpdate: (id: string, c: Partial<Feature>) => void }) {
  return (
    <div className="param-row">
      <label className="param-label">name</label>
      <input
        type="text"
        className="param-input feature-name-input"
        value={feature.name}
        onChange={(e) => onUpdate(feature.id, { name: e.target.value })}
      />
    </div>
  );
}

// ── Per-type property groups ────────────────────────────

function BoxProps({ feature, onUpdate }: { feature: BoxFeature; onUpdate: (id: string, c: Partial<Feature>) => void }) {
  return (
    <>
      <div className="feature-property-section">Dimensions</div>
      <DimRow label="width" value={feature.width} onChange={(v) => onUpdate(feature.id, { width: v } as Partial<Feature>)} />
      <DimRow label="height" value={feature.height} onChange={(v) => onUpdate(feature.id, { height: v } as Partial<Feature>)} />
      <DimRow label="depth" value={feature.depth} onChange={(v) => onUpdate(feature.id, { depth: v } as Partial<Feature>)} />
    </>
  );
}

function CylinderProps({ feature, onUpdate }: { feature: CylinderFeature; onUpdate: (id: string, c: Partial<Feature>) => void }) {
  return (
    <>
      <div className="feature-property-section">Dimensions</div>
      <DimRow label="radius" value={feature.radius} onChange={(v) => onUpdate(feature.id, { radius: v } as Partial<Feature>)} />
      <DimRow label="height" value={feature.height} onChange={(v) => onUpdate(feature.id, { height: v } as Partial<Feature>)} />
    </>
  );
}

function SphereProps({ feature, onUpdate }: { feature: SphereFeature; onUpdate: (id: string, c: Partial<Feature>) => void }) {
  return (
    <>
      <div className="feature-property-section">Dimensions</div>
      <DimRow label="radius" value={feature.radius} onChange={(v) => onUpdate(feature.id, { radius: v } as Partial<Feature>)} />
    </>
  );
}

/** Shared ref dropdown — used by both BooleanProps and ModifierProps. */
function RefSelect({
  featureId,
  label,
  refKey,
  currentRef,
  priorFeatures,
  onUpdate,
}: {
  featureId: string;
  label: string;
  refKey: string;
  currentRef: string;
  priorFeatures: Feature[];
  onUpdate: (id: string, c: Partial<Feature>) => void;
}) {
  return (
    <div className="param-row">
      <label className="param-label">{label}</label>
      <select
        className="param-input boolean-select"
        value={currentRef}
        onChange={(e) => onUpdate(featureId, { [refKey]: e.target.value } as Partial<Feature>)}
      >
        {!priorFeatures.some((f) => f.id === currentRef) && (
          <option value={currentRef} disabled>(invalid ref)</option>
        )}
        {priorFeatures.map((f) => (
          <option key={f.id} value={f.id}>{f.name}</option>
        ))}
      </select>
    </div>
  );
}

function BooleanProps({
  feature,
  features,
  onUpdate,
}: {
  feature: BooleanFeature;
  features: Feature[];
  onUpdate: (id: string, c: Partial<Feature>) => void;
}) {
  const idx = features.findIndex((f) => f.id === feature.id);
  const priorFeatures = features.slice(0, idx);

  return (
    <>
      <div className="feature-property-section">Operation: {feature.type}</div>
      <RefSelect featureId={feature.id} label="target" refKey="targetRef" currentRef={feature.targetRef} priorFeatures={priorFeatures} onUpdate={onUpdate} />
      <RefSelect featureId={feature.id} label="tool" refKey="toolRef" currentRef={feature.toolRef} priorFeatures={priorFeatures} onUpdate={onUpdate} />
    </>
  );
}

const MODIFIER_PARAM: Record<string, { label: string; key: string }> = {
  fillet: { label: "radius", key: "radius" },
  chamfer: { label: "distance", key: "distance" },
  shell: { label: "thickness", key: "thickness" },
};

/** Compute the max safe value for a modifier based on its target's smallest dimension. */
function getModifierMax(feature: ModifierFeature, features: Feature[]): number {
  const target = features.find((f) => f.id === feature.targetRef);
  if (!target) return 10;

  let minDim = Infinity;
  if (target.type === "box") {
    minDim = Math.min(target.width, target.height, target.depth);
  } else if (target.type === "cylinder") {
    minDim = Math.min(target.radius * 2, target.height);
  } else if (target.type === "sphere") {
    minDim = target.radius * 2;
  } else if (target.type === "extrude") {
    const ef = target as ExtrudeFeature;
    const w = ef.profileWidth ?? (ef.profile === "circle" ? (ef.profileRadius ?? 10) * 2 : 20);
    const h = ef.profileHeight ?? (ef.profile === "circle" ? (ef.profileRadius ?? 10) * 2 : 20);
    minDim = Math.min(w, h, ef.depth);
  } else if (target.type === "loft") {
    const lf = target as LoftFeature;
    minDim = Math.min(lf.startWidth, lf.startHeight, lf.endWidth, lf.endHeight, lf.height);
  } else {
    return 10; // conservative fallback for boolean/modifier targets
  }

  // Fillet/chamfer: max 50% of smallest dimension. Shell: max 40% (needs wall clearance).
  const ratio = feature.type === "shell" ? 0.4 : 0.5;
  return Math.max(0.1, Math.round(minDim * ratio * 10) / 10);
}

function ModifierProps({
  feature,
  features,
  onUpdate,
}: {
  feature: ModifierFeature;
  features: Feature[];
  onUpdate: (id: string, c: Partial<Feature>) => void;
}) {
  const idx = features.findIndex((f) => f.id === feature.id);
  const priorFeatures = features.slice(0, idx);

  if (feature.type === "draft") {
    const df = feature as import("./featureTypes").DraftFeature;
    return (
      <>
        <div className="feature-property-section">Modifier: draft</div>
        <RefSelect featureId={feature.id} label="target" refKey="targetRef" currentRef={df.targetRef} priorFeatures={priorFeatures} onUpdate={onUpdate} />
        <DimRow label="angle °" value={df.angle} min={0.5} max={30} onChange={(v) => onUpdate(feature.id, { angle: v } as Partial<Feature>)} />
        <AxisSelect value={df.axis} onChange={(v) => onUpdate(feature.id, { axis: v } as Partial<Feature>)} />
      </>
    );
  }

  const param = MODIFIER_PARAM[feature.type];
  const max = getModifierMax(feature, features);
  const currentValue = (feature as any)[param.key] as number;
  const clampedValue = Math.min(currentValue, max);
  if (clampedValue !== currentValue) {
    onUpdate(feature.id, { [param.key]: clampedValue } as Partial<Feature>);
  }

  return (
    <>
      <div className="feature-property-section">Modifier: {feature.type}</div>
      <RefSelect featureId={feature.id} label="target" refKey="targetRef" currentRef={feature.targetRef} priorFeatures={priorFeatures} onUpdate={onUpdate} />
      <DimRow
        label={param.label}
        value={clampedValue}
        min={0.1}
        max={max}
        onChange={(v) => onUpdate(feature.id, { [param.key]: v } as Partial<Feature>)}
      />
    </>
  );
}

function ExtrudeProps({
  feature,
  onUpdate,
}: {
  feature: ExtrudeFeature;
  onUpdate: (id: string, c: Partial<Feature>) => void;
}) {
  return (
    <>
      <div className="feature-property-section">Profile: {feature.profile}</div>
      <DimRow label="depth" value={feature.depth} min={0.1} max={200} onChange={(v) => onUpdate(feature.id, { depth: v } as Partial<Feature>)} />
      {feature.profile !== "circle" && (
        <>
          <DimRow label="width" value={feature.profileWidth ?? 20} onChange={(v) => onUpdate(feature.id, { profileWidth: v } as Partial<Feature>)} />
          <DimRow label="height" value={feature.profileHeight ?? 20} onChange={(v) => onUpdate(feature.id, { profileHeight: v } as Partial<Feature>)} />
        </>
      )}
      {feature.profile === "circle" && (
        <DimRow label="radius" value={feature.profileRadius ?? 10} onChange={(v) => onUpdate(feature.id, { profileRadius: v } as Partial<Feature>)} />
      )}
      {feature.profile === "rounded-rectangle" && (
        <DimRow label="corner r" value={feature.profileRadius ?? 3} min={0.1} max={Math.min((feature.profileWidth ?? 20), (feature.profileHeight ?? 20)) / 2} onChange={(v) => onUpdate(feature.id, { profileRadius: v } as Partial<Feature>)} />
      )}
      <div className="feature-property-section">Twist / Taper</div>
      <DimRow label="twist °" value={feature.twistAngle ?? 0} min={0} max={360} onChange={(v) => onUpdate(feature.id, { twistAngle: v } as Partial<Feature>)} />
      <DimRow label="taper" value={feature.taperFactor ?? 1} min={0} max={1} onChange={(v) => onUpdate(feature.id, { taperFactor: Math.round(v * 100) / 100 } as Partial<Feature>)} />
    </>
  );
}

function ProfileSelect({ label, value, onChange }: { label: string; value: ProfileType; onChange: (v: ProfileType) => void }) {
  return (
    <div className="param-row">
      <label className="param-label">{label}</label>
      <select className="param-input boolean-select" value={value} onChange={(e) => onChange(e.target.value as ProfileType)}>
        <option value="rectangle">Rectangle</option>
        <option value="circle">Circle</option>
        <option value="rounded-rectangle">Rounded Rect</option>
      </select>
    </div>
  );
}

function LoftProps({ feature, onUpdate }: { feature: LoftFeature; onUpdate: (id: string, c: Partial<Feature>) => void }) {
  return (
    <>
      <div className="feature-property-section">Start Profile</div>
      <ProfileSelect label="shape" value={feature.startProfile} onChange={(v) => onUpdate(feature.id, { startProfile: v } as Partial<Feature>)} />
      <DimRow label="width" value={feature.startWidth} max={100} onChange={(v) => onUpdate(feature.id, { startWidth: v } as Partial<Feature>)} />
      <DimRow label="height" value={feature.startHeight} max={100} onChange={(v) => onUpdate(feature.id, { startHeight: v } as Partial<Feature>)} />
      <div className="feature-property-section">End Profile</div>
      <ProfileSelect label="shape" value={feature.endProfile} onChange={(v) => onUpdate(feature.id, { endProfile: v } as Partial<Feature>)} />
      <DimRow label="width" value={feature.endWidth} max={100} onChange={(v) => onUpdate(feature.id, { endWidth: v } as Partial<Feature>)} />
      <DimRow label="height" value={feature.endHeight} max={100} onChange={(v) => onUpdate(feature.id, { endHeight: v } as Partial<Feature>)} />
      <div className="feature-property-section">Loft</div>
      <DimRow label="height" value={feature.height} min={1} max={200} onChange={(v) => onUpdate(feature.id, { height: v } as Partial<Feature>)} />
      <div className="param-row">
        <label className="param-label">ruled</label>
        <input type="checkbox" checked={feature.ruled} onChange={(e) => onUpdate(feature.id, { ruled: e.target.checked } as Partial<Feature>)} />
      </div>
    </>
  );
}

// ── Shared selectors ─────────────────────────────────────

function AxisSelect({ label = "axis", value, onChange }: { label?: string; value: AxisName; onChange: (v: AxisName) => void }) {
  return (
    <div className="param-row">
      <label className="param-label">{label}</label>
      <select className="param-input boolean-select" value={value} onChange={(e) => onChange(e.target.value as AxisName)}>
        <option value="X">X</option>
        <option value="Y">Y</option>
        <option value="Z">Z</option>
      </select>
    </div>
  );
}

function PlaneSelect({ value, onChange }: { value: MirrorPlaneName; onChange: (v: MirrorPlaneName) => void }) {
  return (
    <div className="param-row">
      <label className="param-label">plane</label>
      <select className="param-input boolean-select" value={value} onChange={(e) => onChange(e.target.value as MirrorPlaneName)}>
        <option value="XY">XY</option>
        <option value="XZ">XZ</option>
        <option value="YZ">YZ</option>
      </select>
    </div>
  );
}

// ── Phase E property groups ──────────────────────────────

function RevolveProps({ feature, onUpdate }: { feature: RevolveFeature; onUpdate: (id: string, c: Partial<Feature>) => void }) {
  return (
    <>
      <div className="feature-property-section">Profile: {feature.profile}</div>
      <AxisSelect value={feature.axis} onChange={(v) => onUpdate(feature.id, { axis: v } as Partial<Feature>)} />
      <DimRow label="angle °" value={feature.angle ?? 360} min={10} max={360} onChange={(v) => onUpdate(feature.id, { angle: Math.round(v) } as Partial<Feature>)} />
      <DimRow label="offset" value={feature.offset} min={1} max={100} onChange={(v) => onUpdate(feature.id, { offset: v } as Partial<Feature>)} />
      {feature.profile !== "circle" && (
        <>
          <DimRow label="width" value={feature.profileWidth ?? 10} max={50} onChange={(v) => onUpdate(feature.id, { profileWidth: v } as Partial<Feature>)} />
          <DimRow label="height" value={feature.profileHeight ?? 5} max={50} onChange={(v) => onUpdate(feature.id, { profileHeight: v } as Partial<Feature>)} />
        </>
      )}
      {feature.profile === "circle" && (
        <DimRow label="radius" value={feature.profileRadius ?? 3} max={30} onChange={(v) => onUpdate(feature.id, { profileRadius: v } as Partial<Feature>)} />
      )}
      {feature.profile === "rounded-rectangle" && (
        <DimRow label="corner r" value={feature.profileRadius ?? 1} min={0.1} max={Math.min((feature.profileWidth ?? 10), (feature.profileHeight ?? 5)) / 2} onChange={(v) => onUpdate(feature.id, { profileRadius: v } as Partial<Feature>)} />
      )}
    </>
  );
}

function SweepProps({ feature, onUpdate }: { feature: SweepFeature; onUpdate: (id: string, c: Partial<Feature>) => void }) {
  return (
    <>
      <div className="feature-property-section">Helix Sweep</div>
      <DimRow label="wire r" value={feature.profileRadius} min={0.1} max={10} onChange={(v) => onUpdate(feature.id, { profileRadius: v } as Partial<Feature>)} />
      <DimRow label="coil r" value={feature.helixRadius} min={1} max={50} onChange={(v) => onUpdate(feature.id, { helixRadius: v } as Partial<Feature>)} />
      <DimRow label="pitch" value={feature.pitch} min={1} max={50} onChange={(v) => onUpdate(feature.id, { pitch: v } as Partial<Feature>)} />
      <DimRow label="turns" value={feature.turns} min={1} max={20} onChange={(v) => onUpdate(feature.id, { turns: Math.round(v) } as Partial<Feature>)} />
    </>
  );
}

function TransformProps({ feature, features, onUpdate }: { feature: TransformFeature; features: Feature[]; onUpdate: (id: string, c: Partial<Feature>) => void }) {
  const idx = features.findIndex((f) => f.id === feature.id);
  const priorFeatures = features.slice(0, idx);

  if (feature.type === "mirror") {
    const mf = feature as MirrorFeature;
    return (
      <>
        <div className="feature-property-section">Mirror</div>
        <RefSelect featureId={feature.id} label="target" refKey="targetRef" currentRef={mf.targetRef} priorFeatures={priorFeatures} onUpdate={onUpdate} />
        <PlaneSelect value={mf.plane} onChange={(v) => onUpdate(feature.id, { plane: v } as Partial<Feature>)} />
      </>
    );
  }
  if (feature.type === "linearPattern") {
    const lp = feature as LinearPatternFeature;
    return (
      <>
        <div className="feature-property-section">Linear Pattern</div>
        <RefSelect featureId={feature.id} label="target" refKey="targetRef" currentRef={lp.targetRef} priorFeatures={priorFeatures} onUpdate={onUpdate} />
        <AxisSelect value={lp.axis} onChange={(v) => onUpdate(feature.id, { axis: v } as Partial<Feature>)} />
        <DimRow label="count" value={lp.count} min={2} max={20} onChange={(v) => onUpdate(feature.id, { count: Math.round(v) } as Partial<Feature>)} />
        <DimRow label="spacing" value={lp.spacing} min={1} max={200} onChange={(v) => onUpdate(feature.id, { spacing: v } as Partial<Feature>)} />
      </>
    );
  }
  // circularPattern
  const cp = feature as CircularPatternFeature;
  return (
    <>
      <div className="feature-property-section">Circular Pattern</div>
      <RefSelect featureId={feature.id} label="target" refKey="targetRef" currentRef={cp.targetRef} priorFeatures={priorFeatures} onUpdate={onUpdate} />
      <AxisSelect value={cp.axis} onChange={(v) => onUpdate(feature.id, { axis: v } as Partial<Feature>)} />
      <DimRow label="count" value={cp.count} min={2} max={20} onChange={(v) => onUpdate(feature.id, { count: Math.round(v) } as Partial<Feature>)} />
      <DimRow label="angle" value={cp.angle} min={10} max={360} onChange={(v) => onUpdate(feature.id, { angle: v } as Partial<Feature>)} />
    </>
  );
}

function SheetMetalProps({ feature, features, onUpdate }: {
  feature: import("./featureTypes").SheetMetalFeature;
  features: Feature[];
  onUpdate: (id: string, c: Partial<Feature>) => void;
}) {
  if (feature.type === "sheetMetalBase") {
    const f = feature as import("./featureTypes").SheetMetalBaseFeature;
    return (
      <>
        <div className="feature-property-section">Sheet Metal Base</div>
        <DimRow label="width" value={f.width} min={5} max={500} onChange={(v) => onUpdate(f.id, { width: v } as Partial<Feature>)} />
        <DimRow label="length" value={f.length} min={5} max={500} onChange={(v) => onUpdate(f.id, { length: v } as Partial<Feature>)} />
        <DimRow label="thickness" value={f.thickness} min={0.3} max={10} onChange={(v) => onUpdate(f.id, { thickness: v } as Partial<Feature>)} />
        <DimRow label="bend R" value={f.bendRadius} min={0.5} max={20} onChange={(v) => onUpdate(f.id, { bendRadius: v } as Partial<Feature>)} />
        <DimRow label="K-factor" value={f.kFactor} min={0.2} max={0.6} onChange={(v) => onUpdate(f.id, { kFactor: v } as Partial<Feature>)} />
      </>
    );
  }
  if (feature.type === "sheetMetalCornerRelief") {
    const cr = feature as import("./featureTypes").SheetMetalCornerReliefFeature;
    const idx = features.findIndex((ff) => ff.id === feature.id);
    const priorSm = features.slice(0, idx).filter((ff) => ff.type === "sheetMetalBase" || ff.type === "sheetMetalFlange");
    return (
      <>
        <div className="feature-property-section">Corner Relief</div>
        <RefSelect featureId={feature.id} label="target" refKey="targetRef" currentRef={cr.targetRef} priorFeatures={priorSm} onUpdate={onUpdate} />
        <div className="param-row">
          <label className="param-label">type</label>
          <select className="param-input boolean-select" value={cr.reliefType}
            onChange={(e) => onUpdate(cr.id, { reliefType: e.target.value } as Partial<Feature>)}>
            <option value="circular">Circular</option>
            <option value="square">Square</option>
            <option value="obround">Obround</option>
          </select>
        </div>
        <DimRow label="scale" value={cr.reliefScale} min={0.5} max={3} onChange={(v) => onUpdate(cr.id, { reliefScale: v } as Partial<Feature>)} />
      </>
    );
  }
  // sheetMetalFlange
  const fl = feature as import("./featureTypes").SheetMetalFlangeFeature;
  const idx = features.findIndex((f) => f.id === feature.id);
  const priorSheetMetal = features.slice(0, idx).filter((f) => f.type === "sheetMetalBase" || f.type === "sheetMetalFlange");
  return (
    <>
      <div className="feature-property-section">Edge Flange</div>
      <RefSelect featureId={feature.id} label="base" refKey="targetRef" currentRef={fl.targetRef} priorFeatures={priorSheetMetal} onUpdate={onUpdate} />
      <DimRow label="length" value={fl.flangeLength} min={2} max={200} onChange={(v) => onUpdate(fl.id, { flangeLength: v } as Partial<Feature>)} />
      <DimRow label="angle" value={fl.flangeAngle} min={1} max={180} onChange={(v) => onUpdate(fl.id, { flangeAngle: v } as Partial<Feature>)} />
      <div className="param-row">
        <label className="param-label">edge</label>
        <select
          className="param-input boolean-select"
          value={fl.edge}
          onChange={(e) => onUpdate(fl.id, { edge: e.target.value } as Partial<Feature>)}
        >
          <option value="right">Right</option>
          <option value="left">Left</option>
          <option value="top">Top</option>
          <option value="bottom">Bottom</option>
        </select>
      </div>
    </>
  );
}

function AssemblyPatternProps({ feature, features, onUpdate }: {
  feature: import("./featureTypes").AssemblyPatternFeature;
  features: Feature[];
  onUpdate: (id: string, c: Partial<Feature>) => void;
}) {
  const idx = features.findIndex((f) => f.id === feature.id);
  const priorImported = features.slice(0, idx).filter((f) => f.type === "importedPart");

  if (feature.type === "assemblyLinearPattern") {
    return (
      <>
        <div className="feature-property-section">Linear Array</div>
        <RefSelect featureId={feature.id} label="part" refKey="targetRef" currentRef={feature.targetRef} priorFeatures={priorImported} onUpdate={onUpdate} />
        <AxisSelect value={feature.axis} onChange={(v) => onUpdate(feature.id, { axis: v } as Partial<Feature>)} />
        <DimRow label="count" value={feature.count} min={2} max={20} onChange={(v) => onUpdate(feature.id, { count: Math.round(v) } as Partial<Feature>)} />
        <DimRow label="spacing" value={feature.spacing} min={1} max={200} onChange={(v) => onUpdate(feature.id, { spacing: v } as Partial<Feature>)} />
      </>
    );
  }
  // assemblyCircularPattern
  return (
    <>
      <div className="feature-property-section">Circular Array</div>
      <RefSelect featureId={feature.id} label="part" refKey="targetRef" currentRef={feature.targetRef} priorFeatures={priorImported} onUpdate={onUpdate} />
      <AxisSelect value={feature.axis} onChange={(v) => onUpdate(feature.id, { axis: v } as Partial<Feature>)} />
      <DimRow label="count" value={feature.count} min={2} max={20} onChange={(v) => onUpdate(feature.id, { count: Math.round(v) } as Partial<Feature>)} />
      <DimRow label="angle" value={feature.angle} min={10} max={360} onChange={(v) => onUpdate(feature.id, { angle: v } as Partial<Feature>)} />
    </>
  );
}

function SketchOperationProps({ feature, onUpdate }: { feature: import("./featureTypes").SketchFeature; onUpdate: (id: string, c: Partial<Feature>) => void }) {
  const op = feature.operation ?? "extrude";
  return (
    <>
      <div className="feature-property-section">Sketch Profile</div>
      <div className="param-row">
        <label className="param-label">plane</label>
        <select className="param-input boolean-select" value={feature.plane} onChange={(e) => onUpdate(feature.id, { plane: e.target.value } as Partial<Feature>)}>
          <option value="XY">XY</option>
          <option value="XZ">XZ</option>
          <option value="YZ">YZ</option>
        </select>
      </div>
      <div className="param-row">
        <label className="param-label">points</label>
        <span className="param-input" style={{ fontSize: 11, color: "var(--text-muted)" }}>{feature.commands.length} commands · {feature.closed ? "closed" : "open"}</span>
      </div>
      <div className="feature-property-section">Operation</div>
      <div className="param-row">
        <label className="param-label">type</label>
        <select className="param-input boolean-select" value={op} onChange={(e) => onUpdate(feature.id, { operation: e.target.value } as Partial<Feature>)}>
          <option value="extrude">Extrude</option>
          <option value="revolve">Revolve</option>
        </select>
      </div>
      {op === "extrude" && (
        <>
          <DimRow label="depth" value={feature.depth} min={0.1} max={200} onChange={(v) => onUpdate(feature.id, { depth: v } as Partial<Feature>)} />
          <DimRow label="twist °" value={feature.twistAngle ?? 0} min={0} max={360} onChange={(v) => onUpdate(feature.id, { twistAngle: v } as Partial<Feature>)} />
          <DimRow label="taper" value={feature.taperFactor ?? 1} min={0} max={1} onChange={(v) => onUpdate(feature.id, { taperFactor: Math.round(v * 100) / 100 } as Partial<Feature>)} />
        </>
      )}
      {op === "revolve" && (
        <>
          <AxisSelect value={feature.revolveAxis ?? "Z"} onChange={(v) => onUpdate(feature.id, { revolveAxis: v } as Partial<Feature>)} />
          <DimRow label="angle °" value={feature.revolveAngle ?? 360} min={10} max={360} onChange={(v) => onUpdate(feature.id, { revolveAngle: Math.round(v) } as Partial<Feature>)} />
        </>
      )}
    </>
  );
}

/** Reusable 3-axis slider group for Vec3 fields */
function Vec3Sliders({ label, value, min, max, field, featureId, onUpdate }: {
  label: string; value: Vec3; min: number; max: number;
  field: string; featureId: string;
  onUpdate: (id: string, c: Partial<Feature>) => void;
}) {
  const labels = ["x", "y", "z"];
  const classes = ["axis-x", "axis-y", "axis-z"];
  return (
    <>
      <div className="feature-property-section">{label}</div>
      {labels.map((l, i) => (
        <DimRow key={l} label={label === "Rotation" ? `r${l}` : label === "Explode Offset" ? `d${l}` : l}
          value={value[i]} min={min} max={max} labelClass={classes[i]}
          onChange={(v) => {
            const updated = [...value] as Vec3;
            updated[i] = v;
            onUpdate(featureId, { [field]: updated } as Partial<Feature>);
          }} />
      ))}
    </>
  );
}

function MateParamSlider({ label, min, max, step, value, onChange }: {
  label: string; min: number; max: number; step: number; value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="param-row mate-param-row">
      <label className="param-label">{label}</label>
      <input type="range" className="param-slider" min={min} max={max} step={step}
        value={value} onChange={(e) => onChange(Number(e.target.value))} />
      <input type="number" className="param-number" step={step} min={min} max={max}
        value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </div>
  );
}

const MATE_LABELS: Record<string, string> = {
  faceToFace: "Face-to-Face",
  coincident: "Coincident",
  fastened: "Fastened",
  revolute: "Revolute",
  slider: "Slider",
};

function ImportedPartProps({ feature, onUpdate, onRemoveMate, onUpdateMate }: {
  feature: import("./featureTypes").ImportedPartFeature;
  onUpdate: (id: string, c: Partial<Feature>) => void;
  onRemoveMate?: (featureId: string, mateId: string) => void;
  onUpdateMate?: (featureId: string, mateId: string, changes: Partial<MateConstraint>) => void;
}) {
  const mates = feature.mates ?? [];
  const config = feature.configuration ?? {};

  // Find standard part definition if this is one
  const stdMatch = feature.importDocId.match(/^std:([^/]+)-(.+)$/);
  const stdDef = stdMatch ? STANDARD_PARTS_CATALOG.find(p => p.id === stdMatch[1]) : null;

  const updateConfig = (key: string, val: string | number) => {
    const newConfig = { ...config, [key]: val };
    const changes: Partial<import("./featureTypes").ImportedPartFeature> = { configuration: newConfig };

    // If size changed, update docId and regenerate script
    if (key === "size" && stdDef) {
      const newSize = stdDef.sizes.find(s => s.label === val);
      if (newSize) {
        changes.importDocId = stdPartDocId(stdDef.id, newSize.label);
        const params = { ...newSize.params, ...newConfig };
        delete (params as any).size;
        changes.importScript = stdDef.scriptTemplate(params as Record<string, number>);
        changes.name = stdPartName(stdDef, newSize, newConfig as Record<string, number>);
      }
    } else if (stdDef) {
      // Regenerate script with new extra field value
      const currentSize = stdDef.sizes.find(s => s.label === config.size) || stdDef.sizes[stdDef.defaultSizeIndex];
      const params = { ...currentSize.params, ...newConfig };
      delete (params as any).size;
      changes.importScript = stdDef.scriptTemplate(params as Record<string, number>);
      changes.name = stdPartName(stdDef, currentSize, newConfig as Record<string, number>);
    }

    onUpdate(feature.id, changes as Partial<Feature>);
  };

  return (
    <>
      <div className="feature-property-section">Imported Part</div>
      <div className="param-row">
        <label className="param-label">source</label>
        <span className="param-input" style={{ fontSize: 11, color: "var(--text-muted)" }}>{feature.importDocName}</span>
      </div>

      {stdDef && (
        <>
          <div className="feature-property-section">Configuration</div>
          <div className="param-row">
            <label className="param-label">Size</label>
            <select
              className="param-input boolean-select"
              value={config.size || ""}
              onChange={(e) => updateConfig("size", e.target.value)}
            >
              {stdDef.sizes.map(s => <option key={s.label} value={s.label}>{s.label}</option>)}
            </select>
          </div>
          {(stdDef.extraFields ?? []).map(f => (
            f.options ? (
              <div key={f.key} className="param-row">
                <label className="param-label">{f.label}</label>
                <select
                  className="param-input boolean-select"
                  value={(config[f.key] as string) ?? f.default}
                  onChange={(e) => updateConfig(f.key, e.target.value)}
                >
                  {f.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
            ) : (
              <DimRow
                key={f.key}
                label={f.label}
                value={(config[f.key] as number) ?? (f.default as number)}
                min={f.min} max={f.max}
                onChange={(v) => updateConfig(f.key, v)}
              />
            )
          ))}
        </>
      )}

      <div className="param-row">
        <label className="param-label">script</label>
        <button
          className="param-input"
          title="Fetch latest script from server"
          style={{ fontSize: 10, cursor: "pointer", background: "var(--bg-surface)", border: "1px solid var(--border-default)", borderRadius: "var(--radius-sm)", color: "var(--text-secondary)" }}
          onClick={async () => {
            if (feature.importDocId.startsWith("std:")) return; // standard parts updated via config
            try {
              const res = await fetch(`/api/documents/${feature.importDocId}/script`);
              if (res.ok) onUpdate(feature.id, { importScript: await res.text() } as Partial<Feature>);
            } catch { /* ignore */ }
          }}
          disabled={feature.importDocId.startsWith("std:")}
        >
          {feature.importScript ? "Refresh" : "Load script"}
        </button>
      </div>
      <Vec3Sliders label="Position" value={feature.importPosition} min={-200} max={200} field="importPosition" featureId={feature.id} onUpdate={onUpdate} />
      <Vec3Sliders label="Rotation" value={feature.importRotation} min={0} max={360} field="importRotation" featureId={feature.id} onUpdate={onUpdate} />
      <Vec3Sliders label="Explode Offset" value={feature.explosionOffset ?? [0, 0, 0]} min={-100} max={100} field="explosionOffset" featureId={feature.id} onUpdate={onUpdate} />
      {mates.length > 0 && (
        <>
          <div className="feature-property-section">Mates ({mates.length})</div>
          {mates.map((m) => (
            <div key={m.id} className="mate-entry">
              <div className="param-row mate-row">
                <label className="param-label mate-type">{MATE_LABELS[m.type] ?? m.type}</label>
                <span className="mate-source">{m.sourceFeatureId.slice(0, 8)}</span>
                {onRemoveMate && (
                  <button className="mate-delete" onClick={() => onRemoveMate(feature.id, m.id)} title="Remove mate">×</button>
                )}
              </div>
              {m.type === "revolute" && onUpdateMate && (
                <MateParamSlider label="angle" min={0} max={360} step={1} value={m.constraintAngle ?? 0}
                  onChange={(v) => onUpdateMate(feature.id, m.id, { constraintAngle: v })} />
              )}
              {m.type === "slider" && onUpdateMate && (
                <MateParamSlider label="offset" min={-100} max={100} step={0.5} value={m.constraintOffset ?? 0}
                  onChange={(v) => onUpdateMate(feature.id, m.id, { constraintOffset: v })} />
              )}
            </div>
          ))}
        </>
      )}
    </>
  );
}

function MaterialSelect({ feature, onUpdate }: { feature: Feature; onUpdate: (id: string, c: Partial<Feature>) => void }) {
  return (
    <>
      <div className="feature-property-section">Material</div>
      <div className="param-row">
        <label className="param-label">preset</label>
        <select
          className="param-input boolean-select"
          value={feature.materialPreset ?? "default"}
          onChange={(e) => onUpdate(feature.id, { materialPreset: e.target.value } as Partial<Feature>)}
        >
          {PRESET_KEYS.map((key) => (
            <option key={key} value={key}>{MATERIAL_PRESETS_MAP[key]}</option>
          ))}
        </select>
      </div>
    </>
  );
}

function PositionProps({ position, onChange }: { position: Vec3; onChange: (pos: Vec3) => void }) {
  return (
    <>
      <div className="feature-property-section">Position</div>
      <DimRow label="x" value={position[0]} min={-200} max={200} onChange={(v) => onChange([v, position[1], position[2]])} labelClass="axis-x" />
      <DimRow label="y" value={position[1]} min={-200} max={200} onChange={(v) => onChange([position[0], v, position[2]])} labelClass="axis-y" />
      <DimRow label="z" value={position[2]} min={-200} max={200} onChange={(v) => onChange([position[0], position[1], v])} labelClass="axis-z" />
    </>
  );
}
